import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, setApiVendedorId, setAuthToken, clearAuthToken, getAuthToken } from '../services/api';
import { APP_NAME, APP_REGION, STORAGE_KEY, VENDOR_LOCKED } from '../config/app';
import { isNetworkError } from '../utils/offlinePedidoQueue';

const VendorContext = createContext(null);

function readSavedVendor() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v?.id ? v : null;
  } catch {
    return null;
  }
}

export function VendorProvider({ children }) {
  const [vendedor, setVendedor] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionOffline, setSessionOffline] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const savedToken = getAuthToken();
        const savedVendor = readSavedVendor();

        if (savedToken) {
          setAuthToken(savedToken);
          try {
            const res = await api.me();
            if (!cancelled && res.data?.vendedor) {
              setUsuario(res.data.usuario || null);
              setVendedor(res.data.vendedor);
              setSessionOffline(false);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data.vendedor));
              return;
            }
            clearAuthToken();
            localStorage.removeItem(STORAGE_KEY);
          } catch (err) {
            if (savedVendor && !cancelled) {
              setVendedor(savedVendor);
              setSessionOffline(true);
              return;
            }
            if (!isNetworkError(err)) {
              clearAuthToken();
            }
          }
        }
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setApiVendedorId(vendedor?.id || null);
  }, [vendedor]);

  useEffect(() => {
    if (!vendedor || !sessionOffline) return undefined;
    const onOnline = () => setSessionOffline(false);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [vendedor, sessionOffline]);

  async function login(username, password) {
    const res = await api.login(username, password);
    if (!res.data?.vendedor) throw new Error('No se pudo iniciar sesión');
    try {
      localStorage.removeItem('oh_sucursales_cache_v1');
      localStorage.removeItem('oh_cart_v1');
    } catch {
      /* ignore */
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(res.data.vendedor));
    setUsuario(res.data.usuario || null);
    setVendedor(res.data.vendedor);
    setSessionOffline(false);
    return res.data;
  }

  function selectVendedor(v) {
    if (VENDOR_LOCKED) return;
    setVendedor(v);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  }

  async function clearVendedor() {
    if (VENDOR_LOCKED) return;
    try {
      await api.marcarUbicacionOffline().catch(() => {});
      await api.logout();
    } catch {
      clearAuthToken();
    }
    setVendedor(null);
    setUsuario(null);
    setSessionOffline(false);
    localStorage.removeItem(STORAGE_KEY);
    try {
      localStorage.removeItem('oh_sucursales_cache_v1');
      localStorage.removeItem('oh_cart_v1');
    } catch {
      /* ignore */
    }
    setApiVendedorId(null);
  }

  const value = useMemo(
    () => ({
      vendedor,
      usuario,
      loading,
      sessionOffline,
      locked: VENDOR_LOCKED,
      appName: vendedor ? `${vendedor.nombre}${APP_REGION ? ` · ${APP_REGION}` : ''}` : APP_NAME,
      login,
      selectVendedor,
      clearVendedor,
    }),
    [vendedor, usuario, loading, sessionOffline]
  );

  return <VendorContext.Provider value={value}>{children}</VendorContext.Provider>;
}

export function useVendor() {
  const ctx = useContext(VendorContext);
  if (!ctx) throw new Error('useVendor debe usarse dentro de VendorProvider');
  return ctx;
}
